import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { DEFAULT_TITLE_COLOR } from "../utils/titleColors";
import StudyGuideSlideDeck from "./StudyGuideSlideDeck";

function formatDate(value) {
  if (!value) return "No timestamp";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No timestamp";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function parseGeneratedConfigNoteMarkup(html) {
  if (typeof html !== "string" || !html.includes("generated-config-note") || typeof DOMParser === "undefined") {
    return null;
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  const note = doc.querySelector(".generated-config-note");
  if (!note) return null;

  const meta = {};
  note.querySelectorAll(".generated-config-meta p").forEach((row) => {
    const label = row.querySelector("strong")?.textContent?.replace(/:$/, "").trim().toLowerCase();
    const value = row.textContent?.replace(row.querySelector("strong")?.textContent || "", "").trim() || "";
    if (label) meta[label] = value;
  });

  const button = note.querySelector('[data-copy-generated-config="true"]');
  const configText = button?.getAttribute("data-generated-config")
    || note.querySelector('[data-generated-config-text="true"]')?.textContent
    || note.querySelector("pre")?.textContent
    || "";

  return {
    generator: meta.generator || "",
    vendor: meta.vendor || "",
    saved: meta.saved || "",
    user: meta.user || "",
    configText,
  };
}

export default function PublicShareView({ token }) {
  const panelRef = useRef(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState("");

  const handleGeneratedConfigCopy = async (event) => {
    const button = event?.currentTarget instanceof HTMLButtonElement
      ? event.currentTarget
      : (event?.target instanceof Element ? event.target.closest?.('[data-copy-generated-config="true"]') : null);
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();

    const text = button.getAttribute("data-generated-config") || "";
    const originalLabel = button.textContent || "Copy config";
    if (!text.trim()) {
      button.textContent = "No config";
      window.setTimeout(() => {
        button.textContent = originalLabel;
      }, 1500);
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const fallbackArea = document.createElement("textarea");
        fallbackArea.value = text;
        fallbackArea.setAttribute("readonly", "true");
        fallbackArea.style.position = "fixed";
        fallbackArea.style.opacity = "0";
        document.body.appendChild(fallbackArea);
        fallbackArea.select();
        document.execCommand("copy");
        document.body.removeChild(fallbackArea);
      }
      button.textContent = "Copied";
      window.setTimeout(() => {
        button.textContent = originalLabel;
      }, 1500);
    } catch {
      button.textContent = "Copy failed";
      window.setTimeout(() => {
        button.textContent = "Copy config";
      }, 1500);
    }
  };

  const renderGeneratedConfigNote = (html) => {
    const parsed = parseGeneratedConfigNoteMarkup(html);
    if (!parsed) return null;

    return (
      <section className="generated-config-note" data-generator-note="true">
        <div className="generated-config-meta">
          <p><strong>Generator:</strong> {parsed.generator}</p>
          <p><strong>Vendor:</strong> {parsed.vendor}</p>
          <p><strong>Saved:</strong> {parsed.saved}</p>
          <p><strong>User:</strong> {parsed.user}</p>
        </div>
        <div className="generated-config-toolbar">
          <button
            type="button"
            className="generated-config-copy-button"
            data-copy-generated-config="true"
            data-generated-config={parsed.configText}
            onClick={handleGeneratedConfigCopy}
          >
            Copy config
          </button>
        </div>
        <pre data-generated-config-text="true"><code>{parsed.configText}</code></pre>
      </section>
    );
  };

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return undefined;

    const handlePanelClick = (event) => {
      const targetNode = event.target;
      const targetElement = targetNode instanceof Element ? targetNode : targetNode?.parentElement || null;
      if (!targetElement?.closest?.('[data-copy-generated-config="true"]')) return;
      handleGeneratedConfigCopy(event);
    };

    panel.addEventListener("click", handlePanelClick);
    return () => {
      panel.removeEventListener("click", handlePanelClick);
    };
  }, []);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return undefined;

    const buttons = Array.from(panel.querySelectorAll('[data-copy-generated-config="true"]'));
    if (!buttons.length) return undefined;

    const handleButtonClick = (event) => {
      handleGeneratedConfigCopy(event);
    };

    buttons.forEach((button) => {
      button.addEventListener("click", handleButtonClick);
    });

    return () => {
      buttons.forEach((button) => {
        button.removeEventListener("click", handleButtonClick);
      });
    };
  }, [data, expandedId]);

  const renderEntryHTML = (html) => {
    if (typeof html !== "string") return { __html: "" };
    const documentStripped = html
      .replace(/<!doctype[^>]*>/gi, "")
      .replace(/<html[^>]*>/gi, "")
      .replace(/<\/html>/gi, "")
      .replace(/<head[\s\S]*?<\/head>/gi, "")
      .replace(/<\/?(body|main|header|footer)[^>]*>/gi, "");
    const commentsVisible = documentStripped.replace(
      /<!--([\s\S]*?)-->/g,
      (_match, comment) =>
        `<div class="html-comment-note">HTML Comment: ${String(comment).trim() || "Hidden source code text"}</div>`
    );
    const noScripts = commentsVisible
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "");
    const noHandlers = noScripts
      .replace(/on\w+\s*=\s*"[^"]*"/gi, "")
      .replace(/on\w+\s*=\s*'[^']*'/gi, "");
    const normalizedBackgrounds = noHandlers.replace(
      /style\s*=\s*"([^"]*)"/gi,
      (_match, styleValue) => {
        const cleaned = String(styleValue)
          .split(";")
          .map((part) => part.trim())
          .filter(Boolean)
          .filter((part) => !/^background(?:-color)?\s*:/i.test(part))
          .join("; ");
        return cleaned ? `style="${cleaned}"` : "";
      }
    );
    const urlPattern = /(^|[^"'=])(https?:\/\/[^\s<]+)/g;
    const withLinks = normalizedBackgrounds.replace(
      urlPattern,
      (_match, prefix, url) => `${prefix}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
    );
    const withImageFallbacks = withLinks.replace(/<img\b([^>]*)>/gi, (_match, attributes) => {
      const attributeText = String(attributes || "");
      const classMatch = attributeText.match(/\sclass=(["'])(.*?)\1/i);
      const className = classMatch?.[2] ? `${classMatch[2]} note-rendered-image` : "note-rendered-image";
      const cleanedAttributes = attributeText.replace(/\sclass=(["'])(.*?)\1/i, "").trim();
      return `<img ${cleanedAttributes} class="${className}" onerror="this.style.display='none';var p=this.parentElement;if(p&&!p.querySelector('.missing-image-label')){p.classList.add('is-missing-image');var c=document.createElement('div');c.className='missing-image-label';c.textContent='Missing image: ' + (this.getAttribute('alt') || 'Attachment');p.appendChild(c);}" />`;
    });
    return { __html: withImageFallbacks };
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`/api/public/shares/${token}`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || "Failed to load shared title.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) return <div className="app-loading">Loading shared title...</div>;
  if (error) return <div className="feedback-panel error">{error}</div>;
  if (!data?.group) return <div className="feedback-panel">Shared title not found.</div>;

  return (
    <main className="auth-shell">
      <section className="auth-panel public-share-panel">
        <div className="auth-copy">
          <span className="eyebrow">Read-only share</span>
          <h1 style={{ color: data.group.color || DEFAULT_TITLE_COLOR }}>{data.group.icon || "🗂️"} {data.group.title}</h1>
          <p>{data.group.note_count || 0} note{data.group.note_count === 1 ? "" : "s"} inside this shared title.</p>
        </div>
        <section className="group-panel" ref={panelRef}>
          {(data.entries || []).map((entry, index) => (
            <article className="group-entry-card" key={entry.id}>
              <div className="group-entry-index" aria-hidden="true">
                <span>{String(index + 1).padStart(2, "0")}</span>
              </div>
              <div className="group-entry-top">
                <div>
                  <div className="group-entry-title-row">
                    <h3>{entry.title}</h3>
                    <span className={`entry-status-badge is-${entry.status || "active"}`}>{entry.status || "active"}</span>
                  </div>
                  <div className="group-entry-meta">
                    <span>{entry.author || "Shared note"}</span>
                    <span>{formatDate(entry.updated_at || entry.created_at)}</span>
                  </div>
                  {Array.isArray(entry.tags) && entry.tags.length ? (
                    <div className="entry-tag-list">
                      {entry.tags.map((tag) => <span key={`${entry.id}-${tag}`} className="entry-tag-chip">#{tag}</span>)}
                    </div>
                  ) : null}
                </div>
                <div className="note-card-actions">
                  <button className="action-link" onClick={() => setExpandedId((current) => current === entry.id ? "" : entry.id)}>
                    {expandedId === entry.id ? "Collapse" : "Expand"}
                  </button>
                </div>
              </div>
              {expandedId === entry.id ? (
                <div className="group-entry-body">
                  {entry.note?.includes("study-guide-slides")
                    ? <StudyGuideSlideDeck note={entry.note} title={entry.title} />
                    : entry.note?.includes("generated-config-note")
                    ? renderGeneratedConfigNote(entry.note)
                    : <div className="note-rendered-content" dangerouslySetInnerHTML={renderEntryHTML(entry.note)} />}
                </div>
              ) : null}
            </article>
          ))}
        </section>
      </section>
    </main>
  );
}
